import { AddOrEditContactTagModal } from "@/components/features/settings/defaults/contact-tags/AddOrEditContactTagModal";
import { Button, Skeleton, Text, Typography } from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { retrieveContactTagQueryOptions } from "@/lib/data/react-query";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { getMatterTagChipClassNames } from "@/lib/utils/matters/matter-tag-color";
import { cn } from "@/lib/utils/cn";
import { ErrorBoundary } from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { sortBy } from "lodash";
import { Pencil, Plus } from "lucide-react";
import { useMemo, useState } from "react";

function ContactTagsViewDangerous() {
  const { hasActionPermission } = useUser();
  const {
    data: contactTagsData,
    isLoading: isContactTagsLoading,
    isError: isContactTagsError,
  } = useQuery(retrieveContactTagQueryOptions());

  const [isAddingTag, setIsAddingTag] = useState(false);
  const [activeContactTagId, setActiveContactTagId] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const canCreateContactTags = hasActionPermission(
    ACTION_PERMISSION_KEYS.contactTag,
    ACTION_PERMISSION_VALUES.create,
  );
  const canUpdateContactTags = hasActionPermission(
    ACTION_PERMISSION_KEYS.contactTag,
    ACTION_PERMISSION_VALUES.update,
  );

  const contactTags = useMemo(
    () =>
      sortBy(contactTagsData?.data ?? [], (tag) =>
        (tag.label ?? "").toLowerCase(),
      ),
    [contactTagsData?.data],
  );

  const activeContactTag = contactTags.find(
    (tag) => tag.id === activeContactTagId,
  );

  return (
    <div className="w-full">
      <div className="flex flex-col gap-4 border-b pb-6 sm:flex-row sm:items-start sm:justify-between">
        <div className="gap-y-1">
          <Text variant="title">Tags</Text>
        </div>
        {canCreateContactTags ? (
          <Button
            className="shrink-0 self-start"
            onClick={() => {
              setIsAddingTag(true);
            }}
          >
            <Plus className="h-4 w-4" />
            Add tag
          </Button>
        ) : null}
      </div>

      <div className="pt-6">
        {isContactTagsLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} className="h-[88px] rounded-2xl" />
            ))}
          </div>
        ) : isContactTagsError ? (
          <Typography variant="subHeading">
            {serverErrorFallbackText}
          </Typography>
        ) : contactTags.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {contactTags.map((tag) => (
              <button
                key={tag.id}
                type="button"
                className={cn(
                  "group relative flex min-h-[88px] items-center rounded-2xl border px-5 py-4 text-left text-base font-medium transition-all",
                  "focus-visible:ring-ring hover:shadow-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden",
                  getMatterTagChipClassNames(tag.color ?? undefined),
                )}
                onClick={() => {
                  setActiveContactTagId(tag.id);
                  setIsEditing(true);
                }}
              >
                <span className="truncate">{tag.label}</span>
                {canUpdateContactTags ? (
                  <span
                    className={cn(
                      "text-muted-foreground ml-auto inline-flex h-9 w-9 shrink-0 items-center justify-center opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-visible:opacity-100",
                    )}
                  >
                    <span className="sr-only">Edit {tag.label}</span>
                    <Pencil className="h-4 w-4" aria-hidden />
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        ) : (
          <div className="border-border bg-card/60 flex min-h-48 flex-col items-center justify-center rounded-2xl border border-dashed px-6 py-10 text-center">
            <Typography variant="heading" className="text-foreground text-lg">
              No contact tags yet
            </Typography>
            <Typography
              variant="subHeading"
              className="mt-2 max-w-md text-sm text-balance"
            >
              Create tags to organize contacts and make them easier to filter
              later.
            </Typography>
            {canCreateContactTags ? (
              <Button
                className="mt-5"
                onClick={() => {
                  setIsAddingTag(true);
                }}
              >
                <Plus className="h-4 w-4" />
                Add tag
              </Button>
            ) : null}
          </div>
        )}
      </div>

      {activeContactTag ? (
        <AddOrEditContactTagModal
          editing={true}
          activeContactTag={activeContactTag}
          setOpen={setIsEditing}
          setActiveContactTagId={setActiveContactTagId}
          open={isEditing}
          isReadOnly={!canUpdateContactTags}
        />
      ) : null}

      <AddOrEditContactTagModal
        editing={false}
        open={isAddingTag}
        setOpen={setIsAddingTag}
        setActiveContactTagId={setActiveContactTagId}
      />
    </div>
  );
}

export default function ContactTagsView() {
  return (
    <ErrorBoundary
      fallback={
        <Typography variant="subHeading">{serverErrorFallbackText}</Typography>
      }
    >
      <ContactTagsViewDangerous />
    </ErrorBoundary>
  );
}
